import { FC, useCallback, useRef, createElement, useState } from "react";
import { Platform, Pressable, Modal, StyleSheet, ViewStyle } from "react-native";
import { PressableScale } from "pressto";
import { View, Text } from "@src/components";
import RNDateTimePicker, { DateTimePickerEvent } from "@react-native-community/datetimepicker";
import { useUnistyles } from "react-native-unistyles";

import { usePickerField, PickerInput, formatDateForHtmlInput, DATE_CONSTRAINTS } from "../Pickers";

type DateDisplayFormat = "locale" | "dd-MMM-yyyy" | "d-MMMM-yyyy";

interface UniversalDatePickerProps {
  value: string; // ISO date string
  onChange: (isoString: string) => void;
  placeholder?: string;
  size?: "small" | "medium" | "large";
  minimumDate?: Date;
  maximumDate?: Date;
  textColor?: string;
  /** Container form input style */
  containerFormInputStyle?: ViewStyle;
  /** Padding left */
  textInputPaddingLeft?: number;
  showIcon?: boolean;
  /** Date display format. "locale" uses user's locale (default), "dd-MMM-yyyy" shows "26 Mar 2026" */
  dateFormat?: DateDisplayFormat;
}

/**
 * Formats an ISO date string using the user's locale
 * Extracts just the date portion to avoid timezone shifts
 */
function formatDateLocale(dateString: string | undefined): string {
  if (!dateString) return "";
  const datePart = dateString.split("T")[0];
  if (!datePart) return "";
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return "";
  const date = new Date(year, month - 1, day);
  const userLocale = Intl.DateTimeFormat().resolvedOptions().locale;
  return new Intl.DateTimeFormat(userLocale).format(date);
}

function formatDateDDMMMYYYY(dateString: string | undefined): string {
  if (!dateString) return "";
  const datePart = dateString.split("T")[0];
  if (!datePart) return "";
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return "";
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(date);
}

function formatDateDMMMMYYYY(dateString: string | undefined): string {
  if (!dateString) return "";
  const datePart = dateString.split("T")[0];
  if (!datePart) return "";
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return "";
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" }).format(date);
}

function getDateFormatter(format: DateDisplayFormat): (dateString: string | undefined) => string {
  if (format === "dd-MMM-yyyy") return formatDateDDMMMYYYY;
  if (format === "d-MMMM-yyyy") return formatDateDMMMMYYYY;
  return formatDateLocale;
}

export const UniversalDatePicker: FC<UniversalDatePickerProps> = ({
  value,
  onChange,
  placeholder = "Select date",
  size = "medium",
  minimumDate = DATE_CONSTRAINTS.MIN_DATE,
  maximumDate = DATE_CONSTRAINTS.MAX_DATE,
  textColor,
  containerFormInputStyle,
  textInputPaddingLeft,
  showIcon = true,
  dateFormat = "locale",
}) => {
  const { theme } = useUnistyles();
  const formatDateForDisplay = getDateFormatter(dateFormat);
  const [isVisible, setIsVisible] = useState(false);
  // Track the temporary selected date while picker is open
  const [tempDate, setTempDate] = useState<Date | null>(null);

  const show = useCallback(() => {
    // Initialize temp date with current value when opening
    setTempDate(value ? new Date(value) : new Date());
    setIsVisible(true);
  }, [value]);

  const hide = useCallback(() => {
    setIsVisible(false);
    setTempDate(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (tempDate) {
      // Create date at UTC noon to avoid timezone shifts
      const utcDate = new Date(Date.UTC(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate(), 12, 0, 0));
      onChange(utcDate.toISOString());
    }
    hide();
  }, [tempDate, onChange, hide]);

  const handleDateChange = useCallback((event: DateTimePickerEvent, selectedDate?: Date) => {
    if (selectedDate) {
      setTempDate(selectedDate);
    }
  }, []);

  // Web-specific: use a hidden native date input and trigger it programmatically
  const dateInputRef = useRef<HTMLInputElement | null>(null);

  const setDateInputRef = useCallback((element: HTMLInputElement | null) => {
    dateInputRef.current = element;
  }, []);

  const handleWebDateClick = useCallback(() => {
    if (Platform.OS === "web" && dateInputRef.current) {
      // Try showPicker() first (modern browsers), fallback to click()
      if (typeof dateInputRef.current.showPicker === "function") {
        dateInputRef.current.showPicker();
      } else {
        dateInputRef.current.click();
      }
    }
  }, []);

  const handleWebDateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const dateValue = event.target.value;
      if (dateValue) {
        // Parse the date string and create a date at noon UTC to avoid timezone shifts
        const [year, month, day] = dateValue.split("-").map(Number);
        if (year && month && day) {
          const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
          onChange(utcDate.toISOString());
        }
      }
    },
    [onChange],
  );

  if (Platform.OS === "web") {
    const inputValue = value ? formatDateForHtmlInput(value) : "";
    const minValue = formatDateForHtmlInput(minimumDate.toISOString());
    const maxValue = formatDateForHtmlInput(maximumDate.toISOString());

    return (
      <View>
        <Pressable onPress={handleWebDateClick}>
          <PickerInput
            value={value}
            formatter={formatDateForDisplay}
            onPress={handleWebDateClick}
            icon="calendar"
            size={size}
            placeholder={placeholder}
            textColor={textColor}
            containerFormInputStyle={containerFormInputStyle}
            textInputPaddingLeft={textInputPaddingLeft}
            showIcon={showIcon}
          />
        </Pressable>
        {/* Hidden native date input that we trigger programmatically */}
        {createElement("input", {
          ref: setDateInputRef,
          type: "date",
          value: inputValue,
          onChange: handleWebDateChange,
          min: minValue,
          max: maxValue,
          style: {
            position: "absolute",
            opacity: 0,
            pointerEvents: "none",
            width: 0,
            height: 0,
          },
        })}
      </View>
    );
  }

  return (
    <>
      <PickerInput
        value={value}
        formatter={formatDateForDisplay}
        onPress={show}
        icon="calendar"
        size={size}
        placeholder={placeholder}
        textColor={textColor}
        containerFormInputStyle={containerFormInputStyle}
        textInputPaddingLeft={textInputPaddingLeft}
        showIcon={showIcon}
      />
      <Modal visible={isVisible} transparent animationType="fade" onRequestClose={hide}>
        <Pressable style={styles.backdrop} onPress={hide}>
          <Pressable
            style={[styles.container, { backgroundColor: theme.colors.surface }]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={[styles.header, { borderBottomColor: theme.colors.border }]}>
              <PressableScale onPress={hide} style={styles.headerButton}>
                <Text color="navy" textStyle="md/semibold">
                  Cancel
                </Text>
              </PressableScale>
              <PressableScale onPress={handleConfirm} style={styles.headerButton}>
                <Text color="navy" textStyle="md/semibold">
                  Confirm
                </Text>
              </PressableScale>
            </View>
            <View style={styles.pickerContainer}>
              <RNDateTimePicker
                value={tempDate || new Date()}
                mode="date"
                display="inline"
                onChange={handleDateChange}
                minimumDate={minimumDate}
                maximumDate={maximumDate}
                style={styles.picker}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "flex-end",
  },
  container: {
    backgroundColor: "white",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: 34, // Safe area for iPhone
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "#ccc",
  },
  headerButton: {
    padding: 8,
  },
  pickerContainer: {
    alignItems: "center",
    paddingVertical: 16,
  },
  picker: {
    width: 320,
    height: 350,
  },
});
